import { Link } from "react-router-dom";
import { Target, ArrowRight } from "lucide-react";
import BeratungDetailLayout, { BeratungSection } from "@/components/BeratungDetailLayout";

const BeratungStrategieberatung = () => (
  <BeratungDetailLayout
    icon={Target}
    title="Strategieberatung"
    intro="Gemeinsam entwickeln wir tragfähige Strategien, die Ihre Ziele mit den Ressourcen und der Kultur Ihrer Organisation in Einklang bringen – gebündelt in einer starken Erzählung."
    seoTitle="Strategieberatung – Leadership Focus"
    seoDescription="Strategieberatung: Prioritäten klären, Zielkonflikte explizit machen und eine Strategie entwickeln, die entscheidbar und erzählbar ist."
    canonical="https://leadership-focus.de/portfolio/beratung/strategieberatung"
  >
    <BeratungSection heading="Wenn …">
      <p>
        Strategie ist vorhanden – bleibt aber „oben“ oder wird intern unterschiedlich interpretiert.
        Prioritäten konkurrieren, Ressourcen sind knapp, Führung sendet gemischte Signale. HR/OE soll
        Orientierung schaffen und Umsetzung ermöglichen, ohne dass es nach reiner
        Kommunikationskampagne wirkt.
      </p>
    </BeratungSection>

    <BeratungSection heading="Typische Anlässe">
      <ul className="list-disc pl-5 space-y-1">
        <li>Neuausrichtung, Wachstums- oder Konsolidierungsphase</li>
        <li>Zielkonflikte, Überlastung durch zu viele Initiativen</li>
        <li>Strategie wird nicht geglaubt oder nicht verstanden</li>
        <li>Wechsel in Geschäftsführung/Top-Team oder neue Governance</li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Dann …">
      <p>
        … entwickeln wir mit Ihnen eine Strategie, die entscheidbar und erzählbar ist: nicht nur
        „wohin“, sondern auch „wofür nicht“. Wir verbinden Ziele mit Ressourcenrealität und Kultur –
        und übersetzen das in eine starke, anschlussfähige Erzählung, die Führungskräfte in echten
        Gesprächen nutzen können (nicht nur auf Folien).
      </p>
    </BeratungSection>

    <BeratungSection heading="Vorgehen (kompakt)">
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong className="font-semibold text-foreground">Klärung:</strong> Was ist die primäre
          Aufgabe – und was ist das primäre Risiko?
        </li>
        <li>
          <strong className="font-semibold text-foreground">Priorisieren &amp; Abwählen:</strong>{" "}
          Initiativen bündeln, Zielkonflikte explizit machen.
        </li>
        <li>
          <strong className="font-semibold text-foreground">
            Erzählung &amp; Führungstauglichkeit:
          </strong>{" "}
          Narrative Leitplanken + Kommunikationsrituale.
        </li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Ergebnis …">
      <ul className="list-disc pl-5 space-y-1">
        <li>Leitplanken (Wozu / wofür nicht) und klare Prioritäten</li>
        <li>Ein umsetzbarer Strategierahmen, der Ressourcen und Kultur berücksichtigt</li>
        <li>Gemeinsame Sprache, mit der Führung Orientierung geben kann</li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Messsignale">
      <ul className="list-disc pl-5 space-y-1">
        <li>Führungskräfte können Strategie in 1 Minute konsistent erklären</li>
        <li>Prioritätenkonflikte werden früher explizit</li>
        <li>Portfolio wird schlanker / es gibt klare Fokus-Themen</li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Risiko/Nebenwirkung (und Abfederung)">
      <p>
        Strategiearbeit kann „politisch“ werden: Interessen, Status und Verlustängste melden sich.
        Wir federn das ab, indem wir Dissens sichtbar machen, ohne ihn moralisch zu laden – und
        Entscheidungen so rahmen, dass Anschlussfähigkeit erhalten bleibt.
      </p>
    </BeratungSection>

    <BeratungSection heading="Kostenlose Erstberatung">
      <p>
        Wir klären in einem ersten Gespräch, welche Stellhebel (Struktur, Entscheidungspunkte,
        Kommunikationsarchitektur) in Ihrem Kontext den größten Unterschied machen.
      </p>
      <Link
        to="/kontakt"
        className="mt-2 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        Erstgespräch vereinbaren
        <ArrowRight className="h-4 w-4" />
      </Link>
    </BeratungSection>
  </BeratungDetailLayout>
);

export default BeratungStrategieberatung;
